<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

import automationOperationsAPI from 'dashboard/api/automationOperations';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import Button from 'dashboard/components-next/button/Button.vue';
import LaisLedgerDrawer from 'dashboard/components-next/LaisLedgerDrawer.vue';
import LaisActionReasonModal from 'dashboard/components-next/LaisActionReasonModal.vue';

import AutomationCadenceEnrollments from './components/AutomationCadenceEnrollments.vue';
import AutomationCadencePreviewCards from './components/AutomationCadencePreviewCards.vue';
import AutomationCadenceToolbar from './components/AutomationCadenceToolbar.vue';
import AutomationDeadLetterTable from './components/AutomationDeadLetterTable.vue';
import AutomationDeadLetterToolbar from './components/AutomationDeadLetterToolbar.vue';
import AutomationExecutionDetail from './components/AutomationExecutionDetail.vue';
import AutomationHealthAlerts from './components/AutomationHealthAlerts.vue';
import AutomationHealthMetricGrid from './components/AutomationHealthMetricGrid.vue';
import AutomationHealthSignals from './components/AutomationHealthSignals.vue';
import AutomationOperationsHeader from './components/AutomationOperationsHeader.vue';
import AutomationOutboxDestinations from './components/AutomationOutboxDestinations.vue';
import AutomationReconciliationResult from './components/AutomationReconciliationResult.vue';
import AutomationReplayControls from './components/AutomationReplayControls.vue';
import AutomationReplayPlanTable from './components/AutomationReplayPlanTable.vue';
import AutomationReplayResult from './components/AutomationReplayResult.vue';
import AutomationRuleFilters from './components/AutomationRuleFilters.vue';
import AutomationRuleHealthTable from './components/AutomationRuleHealthTable.vue';
import { useAutomationOperationsConfirm } from './components/composables/useAutomationOperationsConfirm';
import { useAutomationOperationsQuery } from './components/composables/useAutomationOperationsQuery';
import { REDACTED_KEYS } from './components/utils/automationOperationsConstants';
import {
  actionKey,
  buildDeadLetterEntry,
  cadenceActionKey,
  extractError,
} from './components/utils/automationOperationsFormatters';

const { t } = useI18n();
const route = useRoute();
// Same permission the automation settings route enforces (meta.permissions:
// ['administrator']). The route already blocks non-admins, but the panel also
// gates every side-effecting operation so a downgraded session can never
// replay/reconcile/transition from a stale view.
const { isAdmin } = useAdmin();
const canOperate = isAdmin;

const health = ref(null);
const cadenceSnapshot = ref(null);
const ruleHealthItems = ref([]);
const ruleHealthMeta = ref({
  page: 1,
  per_page: 10,
  total_count: 0,
  total_pages: 0,
});
const ruleFilters = ref({
  q: '',
  eventName: '',
  status: 'all',
  windowHours: 24,
});
const deadLetters = ref([]);
const dlqMeta = ref({
  page: 1,
  per_page: 20,
  total_count: 0,
  total_pages: 0,
});
const statusFilter = ref('open');
const backfillLimit = ref(50);
const cutoverArtifactPaths = ref('');
const cutoverMinCycles = ref(2);
const backfillPreview = ref(null);
const cutoverPreview = ref(null);
const selectedIds = ref([]);
const bulkReason = ref('');
const drawerDeadLetterId = ref(null);
const isDeadLetterDrawerOpen = ref(false);
const detailById = ref({});
const actionLoading = ref({});
const cadenceActionLoading = ref({});
const isLoadingHealth = ref(false);
const isLoadingCadences = ref(false);
const isBackfillPreviewLoading = ref(false);
const isCutoverPreviewLoading = ref(false);
const isLoadingRuleHealth = ref(false);
const isLoadingDeadLetters = ref(false);
const isBulkLoading = ref(false);
const isExecutionLoading = ref(false);
const isReplayPlanLoading = ref(false);
const isReplayLoading = ref(false);
const isApprovalLoading = ref(false);
const isReconciliationLoading = ref(false);
const operationError = ref('');
const executionDetail = ref(null);
const replayForm = ref({
  executionId: '',
  actionRef: '',
  reason: '',
  force: false,
  newIdempotencyKey: '',
  reconciliationState: 'FAILED',
  reconciliationKey: '',
});
const replayPlan = ref(null);
const replayResult = ref(null);
const reconciliationResult = ref(null);

const {
  routeQuery,
  applyOperationsQuery,
  replaceOperationsQuery,
  isSelfWrittenQuery,
} = useAutomationOperationsQuery({
  statusFilter,
  ruleFilters,
  ruleHealthMeta,
  dlqMeta,
  replayForm,
  drawerDeadLetterId,
});

const cadenceSummary = computed(() => cadenceSnapshot.value?.summary || {});
const cadenceEnrollments = computed(
  () => cadenceSnapshot.value?.enrollments || []
);
const cadenceNextExecutions = computed(
  () => cadenceSnapshot.value?.next_executions || []
);
const cadenceLatestRuns = computed(
  () => cadenceSnapshot.value?.latest_runs || []
);

const healthMetrics = computed(() => {
  const metrics = health.value?.metrics || {};
  return [
    {
      key: 'signals',
      label: t('AUTOMATION.OPERATIONS.HEALTH.SIGNALS'),
      value: health.value?.signals?.length || 0,
    },
    {
      key: 'alerts',
      label: t('AUTOMATION.OPERATIONS.HEALTH.ALERTS'),
      value: health.value?.alerts?.count || 0,
    },
    {
      key: 'circuits',
      label: t('AUTOMATION.OPERATIONS.HEALTH.OPEN_CIRCUITS'),
      value: metrics.automation_limits?.open_circuit_count || 0,
    },
    {
      key: 'rules',
      label: t('AUTOMATION.OPERATIONS.HEALTH.RULE_GROUPS'),
      value: ruleHealthMeta.value.total_count || 0,
    },
    {
      key: 'dlq',
      label: t('AUTOMATION.OPERATIONS.HEALTH.DLQ_OPEN'),
      value: metrics.dead_letter?.open_review_count || 0,
    },
    {
      key: 'outboxDestinations',
      label: t('AUTOMATION.OPERATIONS.HEALTH.DESTINATIONS'),
      value: metrics.outbox?.destinations?.length || 0,
    },
    {
      key: 'cadences',
      label: t('AUTOMATION.OPERATIONS.CADENCES.METRIC_CADENCES'),
      value: cadenceSummary.value.cadence_count || 0,
    },
    {
      key: 'cadenceActive',
      label: t('AUTOMATION.OPERATIONS.CADENCES.METRIC_ACTIVE'),
      value: cadenceSummary.value.active_enrollment_count || 0,
    },
    {
      key: 'cadenceDue',
      label: t('AUTOMATION.OPERATIONS.CADENCES.METRIC_DUE'),
      value: cadenceSummary.value.due_timer_count || 0,
    },
  ];
});

const signals = computed(() => health.value?.signals || []);
const alerts = computed(() => health.value?.alerts?.items || []);
const outboxDestinations = computed(
  () => health.value?.metrics?.outbox?.destinations || []
);
const replayActions = computed(() => replayPlan.value?.actions || []);
const hasExecutionId = computed(() =>
  Boolean(replayForm.value.executionId.toString().trim())
);
const hasActionRef = computed(() => Boolean(replayForm.value.actionRef.trim()));
const hasReconcileKey = computed(() =>
  Boolean(replayForm.value.reconciliationKey.trim())
);
// Read-only drill-down (timeline) — anyone who can see the panel can load it.
const canLoadExecution = hasExecutionId;
// Dry-run preview never emits an effect; when forcing, the backend still
// requires an operational reason (>= 8 chars).
const canPreviewReplay = computed(
  () =>
    hasExecutionId.value &&
    (!replayForm.value.force || replayForm.value.reason.trim().length >= 8)
);
// Side-effecting actions require the administrator permission; the reason is
// captured in the confirmation modal, not inline.
const canRunReplay = computed(() => hasExecutionId.value && canOperate.value);
const canReviewApproval = computed(
  () => hasExecutionId.value && hasActionRef.value && canOperate.value
);
const canReconcile = computed(
  () =>
    hasExecutionId.value &&
    hasActionRef.value &&
    Boolean(replayForm.value.reconciliationState) &&
    hasReconcileKey.value &&
    canOperate.value
);
const hasDeadLetters = computed(() => deadLetters.value.length > 0);
const allVisibleSelected = computed(
  () =>
    hasDeadLetters.value &&
    deadLetters.value.every(item => selectedIds.value.includes(item.id))
);

const activeDeadLetter = computed(
  () =>
    detailById.value[drawerDeadLetterId.value] ||
    deadLetters.value.find(item => item.id === drawerDeadLetterId.value) ||
    null
);

// Map the redaction-safe dead-letter payload into the LaisLedgerDrawer entry
// shape (see buildDeadLetterEntry for the redaction contract).
const deadLetterEntry = computed(() =>
  buildDeadLetterEntry(activeDeadLetter.value)
);

const isSelected = id => selectedIds.value.includes(id);

// `reasonOverride` comes from the confirmation modal for side-effecting calls;
// the dry-run preview falls back to the inline reason (needed only for force).
const replayPayload = (reasonOverride = null) => {
  const payload = {
    reason: (reasonOverride ?? replayForm.value.reason).trim(),
    force: replayForm.value.force,
  };
  if (replayForm.value.actionRef.trim()) {
    payload.action_ref = replayForm.value.actionRef.trim();
  }
  if (replayForm.value.newIdempotencyKey.trim()) {
    payload.new_idempotency_key = replayForm.value.newIdempotencyKey.trim();
  }
  return payload;
};

const approvalPayload = (reasonOverride = null) => ({
  action_ref: replayForm.value.actionRef.trim(),
  reason: (reasonOverride ?? replayForm.value.reason).trim(),
});

const reconciliationPayload = (reasonOverride = null) => ({
  action_ref: replayForm.value.actionRef.trim(),
  reason: (reasonOverride ?? replayForm.value.reason).trim(),
  state: replayForm.value.reconciliationState,
  reconciliation_key: replayForm.value.reconciliationKey.trim(),
});

const cutoverArtifactPathList = () =>
  cutoverArtifactPaths.value
    .split(/[\n,]/)
    .map(item => item.trim())
    .filter(Boolean);

const setOperationError = (title, error) => {
  const message = extractError(error);
  operationError.value = message ? `${title}: ${message}` : title;
  useAlert(operationError.value);
};

const ruleHealthParams = page => {
  const params = {
    page,
    per_page: ruleHealthMeta.value.per_page,
    window_hours: ruleFilters.value.windowHours,
  };

  if (ruleFilters.value.q.trim()) {
    params.q = ruleFilters.value.q.trim();
  }
  if (ruleFilters.value.eventName.trim()) {
    params.event_name = ruleFilters.value.eventName.trim();
  }
  if (ruleFilters.value.status !== 'all') {
    params.status = ruleFilters.value.status;
  }

  return params;
};

const fetchHealth = async () => {
  isLoadingHealth.value = true;
  try {
    const { data } = await automationOperationsAPI.health();
    health.value = data;
  } catch (error) {
    setOperationError(t('AUTOMATION.OPERATIONS.HEALTH_ERROR'), error);
  } finally {
    isLoadingHealth.value = false;
  }
};

const fetchCadences = async () => {
  isLoadingCadences.value = true;
  try {
    const { data } = await automationOperationsAPI.cadences({ limit: 50 });
    cadenceSnapshot.value = data;
  } catch (error) {
    setOperationError(t('AUTOMATION.OPERATIONS.CADENCES_ERROR'), error);
  } finally {
    isLoadingCadences.value = false;
  }
};

const fetchRuleHealth = async (
  page = ruleHealthMeta.value.page || 1,
  { syncUrl = true } = {}
) => {
  if (syncUrl) replaceOperationsQuery({ rulePage: page });
  isLoadingRuleHealth.value = true;
  try {
    const { data } = await automationOperationsAPI.ruleHealth(
      ruleHealthParams(page)
    );
    ruleHealthItems.value = data.items || [];
    ruleHealthMeta.value = {
      ...ruleHealthMeta.value,
      ...(data.pagination || {}),
    };
  } catch (error) {
    setOperationError(t('AUTOMATION.OPERATIONS.RULES_ERROR'), error);
  } finally {
    isLoadingRuleHealth.value = false;
  }
};

const fetchDeadLetters = async (
  page = dlqMeta.value.page || 1,
  { syncUrl = true } = {}
) => {
  if (syncUrl) replaceOperationsQuery({ dlqPage: page });
  isLoadingDeadLetters.value = true;
  try {
    const { data } = await automationOperationsAPI.deadLetters({
      status: statusFilter.value,
      page,
      per_page: dlqMeta.value.per_page,
    });
    deadLetters.value = data.payload || [];
    dlqMeta.value = {
      ...dlqMeta.value,
      ...(data.meta || {}),
    };
    selectedIds.value = selectedIds.value.filter(id =>
      deadLetters.value.some(item => item.id === id)
    );
  } catch (error) {
    setOperationError(t('AUTOMATION.OPERATIONS.DLQ_ERROR'), error);
  } finally {
    isLoadingDeadLetters.value = false;
  }
};

const runBackfillPreview = async () => {
  isBackfillPreviewLoading.value = true;
  try {
    const { data } = await automationOperationsAPI.backfillCadencePreview({
      limit: backfillLimit.value,
    });
    backfillPreview.value = data.payload;
    useAlert(t('AUTOMATION.OPERATIONS.BACKFILL_PREVIEW_SUCCESS'));
  } catch (error) {
    setOperationError(t('AUTOMATION.OPERATIONS.BACKFILL_PREVIEW_ERROR'), error);
  } finally {
    isBackfillPreviewLoading.value = false;
  }
};

const runCutoverPreview = async () => {
  isCutoverPreviewLoading.value = true;
  try {
    const { data } = await automationOperationsAPI.cutoverCadencePreview({
      artifact_paths: cutoverArtifactPathList(),
      min_cycles: cutoverMinCycles.value,
    });
    cutoverPreview.value = data.payload;
    useAlert(t('AUTOMATION.OPERATIONS.CUTOVER_PREVIEW_SUCCESS'));
  } catch (error) {
    setOperationError(t('AUTOMATION.OPERATIONS.CUTOVER_PREVIEW_ERROR'), error);
  } finally {
    isCutoverPreviewLoading.value = false;
  }
};

const refreshOperations = async ({
  rulePage = ruleHealthMeta.value.page || 1,
  dlqPage = dlqMeta.value.page || 1,
  syncUrl = true,
} = {}) => {
  operationError.value = '';
  if (syncUrl) replaceOperationsQuery({ rulePage, dlqPage });
  await Promise.all([
    fetchHealth(),
    fetchCadences(),
    fetchRuleHealth(rulePage, { syncUrl: false }),
    fetchDeadLetters(dlqPage, { syncUrl: false }),
  ]);
};

const onRuleFilterSubmit = () => {
  fetchRuleHealth(1);
};

const onStatusFilterChange = () => {
  selectedIds.value = [];
  detailById.value = {};
  isDeadLetterDrawerOpen.value = false;
  drawerDeadLetterId.value = null;
  replaceOperationsQuery({ dlqPage: 1, deadLetterId: null });
  fetchDeadLetters(1);
};

const updateDeadLetter = payload => {
  if (!payload?.id) return;
  deadLetters.value = deadLetters.value.map(item =>
    item.id === payload.id ? { ...item, ...payload } : item
  );
  detailById.value[payload.id] = payload;
};

const executeTransition = async (deadLetter, actionName, reason) => {
  const key = actionKey(deadLetter.id, actionName);
  actionLoading.value[key] = true;
  try {
    const { data } = await automationOperationsAPI.transitionDeadLetter(
      deadLetter.id,
      actionName,
      { reason }
    );
    updateDeadLetter(data.payload);
    useAlert(t('AUTOMATION.OPERATIONS.TRANSITION_SUCCESS'));
    await Promise.all([fetchHealth(), fetchDeadLetters(dlqMeta.value.page)]);
    return true;
  } catch (error) {
    if (error?.response?.data?.payload) {
      updateDeadLetter(error.response.data.payload);
    }
    setOperationError(t('AUTOMATION.OPERATIONS.TRANSITION_ERROR'), error);
    return false;
  } finally {
    actionLoading.value[key] = false;
  }
};

const executeCadenceTransition = async (enrollment, actionName, reason) => {
  const key = cadenceActionKey(enrollment.id, actionName);
  cadenceActionLoading.value[key] = true;
  const request =
    actionName === 'pause'
      ? (id, payload) =>
          automationOperationsAPI.pauseCadenceEnrollment(id, payload)
      : (id, payload) =>
          automationOperationsAPI.resumeCadenceEnrollment(id, payload);
  try {
    await request(enrollment.id, { reason });
    useAlert(t('AUTOMATION.OPERATIONS.CADENCE_TRANSITION_SUCCESS'));
    await fetchCadences();
    return true;
  } catch (error) {
    setOperationError(
      t('AUTOMATION.OPERATIONS.CADENCE_TRANSITION_ERROR'),
      error
    );
    return false;
  } finally {
    cadenceActionLoading.value[key] = false;
  }
};

const bulkTransition = async actionName => {
  isBulkLoading.value = true;
  try {
    const { data } = await automationOperationsAPI.bulkTransitionDeadLetters({
      ids: selectedIds.value,
      action_name: actionName,
      reason: bulkReason.value.trim(),
    });
    (data.payload || []).forEach(result => updateDeadLetter(result.payload));
    useAlert(
      t('AUTOMATION.OPERATIONS.BULK_SUCCESS', {
        count: data.meta?.success_count || 0,
      })
    );
    selectedIds.value = [];
    await Promise.all([fetchHealth(), fetchDeadLetters(dlqMeta.value.page)]);
  } catch (error) {
    setOperationError(t('AUTOMATION.OPERATIONS.BULK_ERROR'), error);
  } finally {
    isBulkLoading.value = false;
  }
};

const toggleSelection = id => {
  selectedIds.value = isSelected(id)
    ? selectedIds.value.filter(selectedId => selectedId !== id)
    : [...selectedIds.value, id];
};

const toggleVisibleSelection = () => {
  selectedIds.value = allVisibleSelected.value
    ? []
    : deadLetters.value.map(item => item.id);
};

const openDeadLetterDrawer = async (deadLetter, { syncUrl = true } = {}) => {
  drawerDeadLetterId.value = deadLetter.id;
  isDeadLetterDrawerOpen.value = true;
  if (syncUrl) replaceOperationsQuery({ deadLetterId: deadLetter.id });
  if (detailById.value[deadLetter.id]) return;

  actionLoading.value[actionKey(deadLetter.id, 'details')] = true;
  try {
    const { data } = await automationOperationsAPI.deadLetter(deadLetter.id);
    detailById.value[deadLetter.id] = data.payload;
  } catch (error) {
    setOperationError(t('AUTOMATION.OPERATIONS.DLQ_ERROR'), error);
  } finally {
    actionLoading.value[actionKey(deadLetter.id, 'details')] = false;
  }
};

const closeDeadLetterDrawer = () => {
  isDeadLetterDrawerOpen.value = false;
  drawerDeadLetterId.value = null;
  replaceOperationsQuery({ deadLetterId: null });
};

const fetchExecutionDetail = async ({ notify = true, syncUrl = true } = {}) => {
  if (!canLoadExecution.value) return;

  if (syncUrl) replaceOperationsQuery();
  isExecutionLoading.value = true;
  try {
    const { data } = await automationOperationsAPI.execution(
      replayForm.value.executionId
    );
    executionDetail.value = data.payload;
    if (notify) {
      useAlert(t('AUTOMATION.OPERATIONS.EXECUTION.DETAIL_SUCCESS'));
    }
  } catch (error) {
    setOperationError(t('AUTOMATION.OPERATIONS.EXECUTION.DETAIL_ERROR'), error);
  } finally {
    isExecutionLoading.value = false;
  }
};

const runReplayPlan = async () => {
  isReplayPlanLoading.value = true;
  replayResult.value = null;
  reconciliationResult.value = null;
  replaceOperationsQuery();
  try {
    const { data } = await automationOperationsAPI.replayPlan(
      replayForm.value.executionId,
      replayPayload()
    );
    replayPlan.value = data.payload;
    useAlert(t('AUTOMATION.OPERATIONS.REPLAY_PLAN_SUCCESS'));
    await fetchExecutionDetail({ notify: false });
  } catch (error) {
    setOperationError(t('AUTOMATION.OPERATIONS.REPLAY_ERROR'), error);
  } finally {
    isReplayPlanLoading.value = false;
  }
};

const executeReplay = async reason => {
  isReplayLoading.value = true;
  try {
    const { data } = await automationOperationsAPI.replay(
      replayForm.value.executionId,
      replayPayload(reason)
    );
    replayResult.value = data.payload;
    useAlert(t('AUTOMATION.OPERATIONS.REPLAY_SUCCESS'));
    await Promise.all([
      fetchHealth(),
      fetchRuleHealth(ruleHealthMeta.value.page),
      fetchExecutionDetail({ notify: false }),
    ]);
    return true;
  } catch (error) {
    setOperationError(t('AUTOMATION.OPERATIONS.REPLAY_ERROR'), error);
    return false;
  } finally {
    isReplayLoading.value = false;
  }
};

// Approve is not destructive (it only green-lights an already-planned action),
// so it stays an inline confirm using the form reason.
const approveAction = async () => {
  isApprovalLoading.value = true;
  try {
    await automationOperationsAPI.approveAction(
      replayForm.value.executionId,
      approvalPayload()
    );
    useAlert(t('AUTOMATION.OPERATIONS.APPROVAL_SUCCESS'));
    await fetchExecutionDetail({ notify: false });
  } catch (error) {
    setOperationError(t('AUTOMATION.OPERATIONS.APPROVAL_ERROR'), error);
  } finally {
    isApprovalLoading.value = false;
  }
};

const executeReject = async reason => {
  isApprovalLoading.value = true;
  try {
    await automationOperationsAPI.rejectAction(
      replayForm.value.executionId,
      approvalPayload(reason)
    );
    useAlert(t('AUTOMATION.OPERATIONS.APPROVAL_SUCCESS'));
    await fetchExecutionDetail({ notify: false });
    return true;
  } catch (error) {
    setOperationError(t('AUTOMATION.OPERATIONS.APPROVAL_ERROR'), error);
    return false;
  } finally {
    isApprovalLoading.value = false;
  }
};

const executeReconcile = async reason => {
  isReconciliationLoading.value = true;
  try {
    const { data } = await automationOperationsAPI.reconcileUnknown(
      replayForm.value.executionId,
      reconciliationPayload(reason)
    );
    reconciliationResult.value = data.payload;
    useAlert(t('AUTOMATION.OPERATIONS.RECONCILIATION_SUCCESS'));
    await Promise.all([
      fetchHealth(),
      fetchRuleHealth(ruleHealthMeta.value.page),
      fetchExecutionDetail({ notify: false }),
    ]);
    return true;
  } catch (error) {
    setOperationError(t('AUTOMATION.OPERATIONS.RECONCILIATION_ERROR'), error);
    return false;
  } finally {
    isReconciliationLoading.value = false;
  }
};

const selectExecutionAction = action => {
  replayForm.value.actionRef = action?.action_ref || '';
  replaceOperationsQuery();
};

const loadQueryDrillDown = async state => {
  if (state.deadLetterId) {
    await openDeadLetterDrawer({ id: state.deadLetterId }, { syncUrl: false });
  } else {
    isDeadLetterDrawerOpen.value = false;
    drawerDeadLetterId.value = null;
  }

  if (state.executionId) {
    await fetchExecutionDetail({ notify: false, syncUrl: false });
  }
};

watch(
  () => route?.query,
  async query => {
    if (isSelfWrittenQuery(query || {})) return;

    const state = applyOperationsQuery(query || {});
    await refreshOperations({
      rulePage: state.rulePage,
      dlqPage: state.dlqPage,
      syncUrl: false,
    });
    await loadQueryDrillDown(state);
  },
  { deep: true }
);

const {
  confirmState,
  isConfirmSubmitting,
  closeConfirm,
  handleConfirm,
  openReplayConfirm,
  openReconcileConfirm,
  openRejectConfirm,
  openTransitionConfirm,
  openCadenceTransitionConfirm,
} = useAutomationOperationsConfirm({
  replayForm,
  canOperate,
  canRunReplay,
  canReconcile,
  canReviewApproval,
  executors: {
    replay: executeReplay,
    reconcile: executeReconcile,
    reject: executeReject,
    dlqTransition: executeTransition,
    cadenceTransition: executeCadenceTransition,
  },
});

onMounted(async () => {
  const state = applyOperationsQuery(routeQuery());
  await refreshOperations({
    rulePage: state.rulePage,
    dlqPage: state.dlqPage,
    syncUrl: false,
  });
  await loadQueryDrillDown(state);
});
</script>

<template>
  <section class="w-full border-y border-n-weak py-5 mb-6">
    <div class="flex flex-col gap-5">
      <AutomationOperationsHeader
        :status="health?.status || ''"
        :generated-at="health?.generated_at || ''"
        :is-refreshing="
          isLoadingHealth ||
          isLoadingCadences ||
          isLoadingRuleHealth ||
          isLoadingDeadLetters
        "
        @refresh="refreshOperations"
      />

      <div
        v-if="operationError"
        class="flex items-start gap-2 border border-n-ruby-5 bg-n-ruby-2 px-3 py-2 text-body-main text-n-ruby-11 rounded-md"
      >
        <span class="i-lucide-circle-alert mt-0.5 size-4 flex-shrink-0" />
        <span class="min-w-0 flex-1 break-words">{{ operationError }}</span>
        <Button
          :label="$t('AUTOMATION.OPERATIONS.RETRY_ACTION')"
          icon="i-lucide-rotate-ccw"
          color="ruby"
          variant="outline"
          size="sm"
          :is-loading="
            isLoadingHealth ||
            isLoadingCadences ||
            isLoadingRuleHealth ||
            isLoadingDeadLetters
          "
          @click="refreshOperations"
        />
      </div>

      <div
        v-if="!canOperate"
        class="flex items-start gap-2 rounded-md border border-n-amber-5 bg-n-amber-2 px-3 py-2 text-body-main text-n-amber-11"
      >
        <span class="i-lucide-shield-alert mt-0.5 size-4 flex-shrink-0" />
        <span class="min-w-0 break-words">
          {{ $t('AUTOMATION.OPERATIONS.PERMISSION_REQUIRED') }}
        </span>
      </div>

      <AutomationHealthMetricGrid :metrics="healthMetrics" />

      <div class="border-t border-n-weak pt-5">
        <AutomationCadenceToolbar
          v-model:backfill-limit="backfillLimit"
          v-model:cutover-artifact-paths="cutoverArtifactPaths"
          v-model:cutover-min-cycles="cutoverMinCycles"
          :summary="cadenceSummary"
          :latest-runs="cadenceLatestRuns"
          :is-backfill-preview-loading="isBackfillPreviewLoading"
          :is-cutover-preview-loading="isCutoverPreviewLoading"
          @backfill-preview="runBackfillPreview"
          @cutover-preview="runCutoverPreview"
        />

        <AutomationCadencePreviewCards
          v-if="backfillPreview || cutoverPreview"
          :backfill-preview="backfillPreview"
          :cutover-preview="cutoverPreview"
        />

        <AutomationCadenceEnrollments
          :enrollments="cadenceEnrollments"
          :next-executions="cadenceNextExecutions"
          :is-loading="isLoadingCadences"
          :can-operate="!!canOperate"
          :action-loading="cadenceActionLoading"
          @transition="openCadenceTransitionConfirm"
        />
      </div>

      <div class="border-t border-n-weak pt-5">
        <AutomationOutboxDestinations
          :destinations="outboxDestinations"
          :is-loading="isLoadingHealth"
        />
      </div>

      <div
        class="grid gap-5 xl:grid-cols-[minmax(0,1.2fr)_minmax(20rem,0.8fr)]"
      >
        <AutomationHealthSignals
          :signals="signals"
          :is-loading="isLoadingHealth"
        />

        <AutomationHealthAlerts
          :alerts="alerts"
          :status="health?.alerts?.status || ''"
        />
      </div>

      <div class="border-t border-n-weak pt-5">
        <AutomationRuleFilters
          v-model:filters="ruleFilters"
          :total-count="ruleHealthMeta.total_count || 0"
          :is-loading="isLoadingRuleHealth"
          @apply="onRuleFilterSubmit"
        />

        <AutomationRuleHealthTable
          :items="ruleHealthItems"
          :is-loading="isLoadingRuleHealth"
          :meta="ruleHealthMeta"
          @page-change="fetchRuleHealth"
        />
      </div>

      <div class="border-t border-n-weak pt-5">
        <AutomationDeadLetterToolbar
          v-model:status-filter="statusFilter"
          v-model:bulk-reason="bulkReason"
          :selected-count="selectedIds.length"
          :can-operate="!!canOperate"
          :is-bulk-loading="isBulkLoading"
          @status-change="onStatusFilterChange"
          @bulk-transition="bulkTransition"
        />

        <AutomationDeadLetterTable
          :dead-letters="deadLetters"
          :is-loading="isLoadingDeadLetters"
          :meta="dlqMeta"
          :selected-ids="selectedIds"
          :all-visible-selected="allVisibleSelected"
          :has-dead-letters="hasDeadLetters"
          :can-operate="!!canOperate"
          :action-loading="actionLoading"
          @toggle-visible-selection="toggleVisibleSelection"
          @toggle-selection="toggleSelection"
          @transition="openTransitionConfirm"
          @open-details="openDeadLetterDrawer"
          @page-change="fetchDeadLetters"
        />
      </div>

      <div class="border-t border-n-weak pt-5">
        <AutomationReplayControls
          v-model:form="replayForm"
          :replay-result-status="replayResult?.status || ''"
          :can-load-execution="canLoadExecution"
          :can-preview-replay="canPreviewReplay"
          :can-run-replay="canRunReplay"
          :can-review-approval="canReviewApproval"
          :can-reconcile="canReconcile"
          :is-execution-loading="isExecutionLoading"
          :is-replay-plan-loading="isReplayPlanLoading"
          :is-replay-loading="isReplayLoading"
          :is-approval-loading="isApprovalLoading"
          :is-reconciliation-loading="isReconciliationLoading"
          @query-change="replaceOperationsQuery()"
          @load-execution="fetchExecutionDetail"
          @plan-replay="runReplayPlan"
          @run-replay="openReplayConfirm"
          @approve="approveAction"
          @reject="openRejectConfirm"
          @reconcile="openReconcileConfirm"
        />

        <AutomationExecutionDetail
          v-if="executionDetail"
          :detail="executionDetail"
          :action-ref="replayForm.actionRef"
          @select-action="selectExecutionAction"
        />

        <AutomationReplayPlanTable
          :actions="replayActions"
          :is-loading="isReplayPlanLoading"
        />

        <AutomationReplayResult v-if="replayResult" :result="replayResult" />

        <AutomationReconciliationResult
          v-if="reconciliationResult"
          :result="reconciliationResult"
        />
      </div>
    </div>

    <LaisLedgerDrawer
      :show="isDeadLetterDrawerOpen"
      :title="$t('AUTOMATION.OPERATIONS.DLQ.DRAWER_TITLE')"
      :entry="deadLetterEntry"
      :redacted-keys="REDACTED_KEYS"
      :provider-label="$t('AUTOMATION.OPERATIONS.DLQ.DRAWER.QUEUE')"
      :destination-label="$t('AUTOMATION.OPERATIONS.DLQ.DRAWER.JOB')"
      :idempotency-label="$t('AUTOMATION.OPERATIONS.DLQ.DRAWER.REVIEW_ACTION')"
      :receipt-label="$t('AUTOMATION.OPERATIONS.DLQ.DRAWER.RUNBOOK')"
      :attempts-label="$t('AUTOMATION.OPERATIONS.DLQ.DRAWER.ATTEMPTS')"
      :payload-label="$t('AUTOMATION.OPERATIONS.DLQ.DRAWER.METADATA')"
      :empty-label="$t('AUTOMATION.OPERATIONS.DLQ.DRAWER.EMPTY')"
      @close="closeDeadLetterDrawer"
    />

    <LaisActionReasonModal
      :show="confirmState.open"
      :title="confirmState.title"
      :description="confirmState.description"
      :consequence="confirmState.consequence"
      :confirm-text="confirmState.confirmText"
      :cancel-text="$t('AUTOMATION.OPERATIONS.CONFIRM.CANCEL')"
      :confirm-variant="confirmState.confirmVariant"
      :reason-required="confirmState.reasonRequired"
      :reason-label="$t('AUTOMATION.OPERATIONS.CONFIRM.REASON_LABEL')"
      :reason-placeholder="
        $t('AUTOMATION.OPERATIONS.CONFIRM.REASON_PLACEHOLDER')
      "
      :is-loading="isConfirmSubmitting"
      @confirm="handleConfirm"
      @close="closeConfirm"
    />
  </section>
</template>
